import { useMemo, useState } from 'react';
import { Calendar, Check, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BottomSheet } from '../components/BottomSheet';
import { Badge, Button } from '../components/ui';
import { ELECTION } from '../lib/election';
import { daysUntil, fmtDateShort, partyColor } from '../lib/utils';
import { formatPartyDisplay } from '../../utils/partyUtils';
import {
  initiativeDisplayName,
  initiativeNumberLine,
} from '../../components/ballot-initiatives/initiativeDisplay';
import { useProfile } from '../hooks/useCitizenData';
import { usePublicEvents } from '../domain/usePublicEvents';
import { toStringArray } from '../lib/interests';
import {
  useBallotMarks,
  useBallotMeasures,
  useBallotRaces,
  type BallotMeasure,
  type BallotRace,
} from '../hooks/useBallot';

type SheetTarget =
  | { kind: 'race'; race: BallotRace }
  | { kind: 'measure'; measure: BallotMeasure }
  | null;

export default function BallotScreen() {
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const { data: measures } = useBallotMeasures();
  const { data: races } = useBallotRaces(profile);
  const { marks, setMark, clearMark } = useBallotMarks();
  const [sheet, setSheet] = useState<SheetTarget>(null);
  const [logisticsOpen, setLogisticsOpen] = useState(false);

  const raceList = useMemo(() => races || [], [races]);
  const interests = useMemo(() => toStringArray((profile as any)?.interests), [profile]);
  // Signature-only update for the web events build; mobile behavior unchanged
  // (interest-ordered feed, no filters) pending the mobile reconciliation.
  const { events: publicEvents } = usePublicEvents({ interests });
  const measureList = useMemo(() => measures || [], [measures]);

  const total = raceList.length + measureList.length;
  const marked = useMemo(() => {
    let n = 0;
    raceList.forEach((r) => marks[r.id] && n++);
    measureList.forEach((m) => marks[`measure-${m.id}`] && n++);
    return n;
  }, [marks, raceList, measureList]);

  const pDays = daysUntil(ELECTION.primary);
  const next = pDays > 0 ? { date: ELECTION.primary, label: 'Primary' } : { date: ELECTION.general, label: 'General' };
  const dleft = daysUntil(next.date);
  const pct = total > 0 ? Math.round((marked / total) * 100) : 0;

  return (
    <div className="flex flex-col screen-enter pb-24">
      {/* Hero */}
      <div
        className="relative text-white px-5 pt-5 pb-6 rounded-b-[24px] overflow-hidden"
        style={{ background: 'linear-gradient(180deg, var(--hero-deep) 0%, var(--hero-deep) 60%, var(--hero-deep-2) 100%)' }}
      >
        <div className="absolute inset-0 topo-overlay pointer-events-none" />
        <div className="relative">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-white/70">
            {next.label} · {new Date(next.date).getFullYear()}
          </p>
          <h1 className="text-[26px] font-extrabold tracking-tight mt-0.5">Your ballot</h1>
          <p className="text-[13px] text-white/85 mt-1.5 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            {profile?.zipCode ? `ZIP ${profile.zipCode}` : 'Set your location'}
            {profile?.houseDistrict ? ` · HD ${profile.houseDistrict}` : ''}
          </p>

          <div className="flex items-center gap-3 mt-4">
            <span
              className="rounded-full px-3 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1.5"
              style={{ background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--hero-light-accent)' }} />
              {dleft} days left
            </span>
            <div className="flex-1 text-right text-[12px] text-white/85 tabular-nums">
              <span className="font-bold text-sm">{marked}</span>
              <span className="opacity-65"> / {total} marked</span>
            </div>
          </div>
          <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: 'rgba(255,255,255,0.14)' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: 'linear-gradient(90deg, var(--hero-light-accent), white)' }}
            />
          </div>
        </div>
      </div>

      {/* District-needed prompt */}
      {raceList.length === 0 && !profile?.houseDistrict && (
        <div className="px-4 pt-4">
          <button
            onClick={() => navigate('/citizen/profile')}
            className="w-full rounded-2xl p-4 text-left border hover-elevate"
            style={{ background: 'var(--accent)', borderColor: 'transparent' }}
          >
            <p className="text-sm font-bold" style={{ color: 'var(--primary)' }}>
              Add your address to see your races
            </p>
            <p className="text-xs text-[color:var(--muted-foreground)] mt-1">
              We'll match candidates to your House and Senate districts. Measures below are on every Colorado ballot.
            </p>
          </button>
        </div>
      )}

      {/* Races */}
      {raceList.length > 0 && (
        <Section title="Races">
          {raceList.map((r) => {
            const pickedId = marks[r.id];
            const picked = r.candidates.find((c) => c.id === pickedId);
            return (
              <Row
                key={r.id}
                marked={!!pickedId}
                title={r.title}
                sub={`${r.candidates.length} candidate${r.candidates.length === 1 ? '' : 's'}`}
                summary={picked ? { text: picked.full_name, color: partyColor(picked.party_affiliation) } : null}
                onTap={() => setSheet({ kind: 'race', race: r })}
              />
            );
          })}
        </Section>
      )}

      {/* Measures */}
      {measureList.length > 0 && (
        <Section title="Ballot measures">
          {measureList.map((m) => {
            const mark = marks[`measure-${m.id}`];
            return (
              <Row
                key={m.id}
                marked={!!mark}
                title={[m.initiative_cycle, initiativeNumberLine(m)].filter(Boolean).join(' · ') || 'Ballot measure'}
                sub={initiativeDisplayName(m)}
                summary={
                  mark
                    ? { text: mark === 'yes' ? 'Voting YES' : 'Voting NO', color: mark === 'yes' ? '#15803D' : '#B91C1C' }
                    : null
                }
                onTap={() => setSheet({ kind: 'measure', measure: m })}
              />
            );
          })}
        </Section>
      )}

      {total === 0 && (
        <p className="text-sm text-[color:var(--muted-foreground)] px-6 py-10 text-center">
          No ballot items posted yet for this election.
        </p>
      )}

      {/* Upcoming public events (calendar PRD O7): statewide + org-published;
          interest matches float to the top, nothing hidden */}
      {publicEvents.length > 0 && (
        <Section title="Upcoming events">
          {publicEvents.slice(0, 6).map((event) => {
            const day = new Date(`${String(event.event_date).slice(0, 10)}T00:00:00`);
            const when = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            const where = event.format === 'virtual' ? 'Virtual' : [event.venue, event.city].filter(Boolean).join(', ');
            const link = event.registration_url || event.link_url;
            const body = (
              <>
                <p className="text-sm font-semibold">{event.title}</p>
                <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">
                  {[when, where, event.host_name].filter(Boolean).join(' · ')}
                </p>
                {event.attribution && (
                  <p className="text-[10px] text-[color:var(--muted-foreground)] mt-1">{event.attribution.text}</p>
                )}
              </>
            );
            return link ? (
              <a
                key={event.id}
                href={link}
                target="_blank"
                rel="noreferrer"
                className="block rounded-2xl p-4 border hover-elevate"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                {body}
              </a>
            ) : (
              <div
                key={event.id}
                className="rounded-2xl p-4 border"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                {body}
              </div>
            );
          })}
        </Section>
      )}

      {/* Logistics */}
      <div className="px-4 pt-4">
        <button
          onClick={() => setLogisticsOpen(!logisticsOpen)}
          className="w-full rounded-2xl p-4 flex items-center gap-3 border hover-elevate text-left"
          style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'var(--accent)', color: 'var(--primary)' }}
          >
            <Calendar className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">How to vote</p>
            <p className="text-xs text-[color:var(--muted-foreground)]">Key dates for this election</p>
          </div>
          <ChevronDown
            className="w-4 h-4 text-[color:var(--muted-foreground)] transition-transform"
            style={{ transform: logisticsOpen ? 'rotate(180deg)' : 'none' }}
          />
        </button>
        {logisticsOpen && (
          <div className="mt-2 rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
            <div className="p-4 space-y-2">
              <KeyDate label="Primary election" date={ELECTION.primary} />
              <KeyDate label="General election" date={ELECTION.general} />
            </div>
          </div>
        )}
      </div>

      {/* Selection sheet */}
      <BottomSheet open={!!sheet} onClose={() => setSheet(null)}>
        {sheet?.kind === 'race' && (
          <RaceSheet
            race={sheet.race}
            picked={marks[sheet.race.id]}
            onPick={(cid) => {
              setMark(sheet.race.id, cid);
            }}
            onClear={() => clearMark(sheet.race.id)}
            onView={(cid) => {
              setSheet(null);
              navigate(`/citizen/candidate/${cid}`);
            }}
          />
        )}
        {sheet?.kind === 'measure' && (
          <MeasureSheet
            measure={sheet.measure}
            picked={marks[`measure-${sheet.measure.id}`]}
            onPick={(v) => setMark(`measure-${sheet.measure.id}`, v)}
            onClear={() => clearMark(`measure-${sheet.measure.id}`)}
          />
        )}
      </BottomSheet>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="pt-2">
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{ background: 'color-mix(in oklab, var(--secondary), transparent 40%)' }}
      >
        <span className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--primary)]">{title}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({
  marked,
  title,
  sub,
  summary,
  onTap,
}: {
  marked: boolean;
  title: string;
  sub: string;
  summary: { text: string; color: string } | null;
  onTap: () => void;
}) {
  return (
    <button
      onClick={onTap}
      className="w-full text-left px-4 py-3.5 flex items-start gap-3 border-b hover-elevate"
      style={{ borderColor: 'var(--border)', background: 'var(--background)' }}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 border"
        style={
          marked
            ? { background: '#DCFCE7', borderColor: '#86EFAC' }
            : { background: 'var(--secondary)', borderColor: 'var(--border)' }
        }
      >
        {marked ? (
          <Check className="w-3.5 h-3.5" style={{ color: '#15803D' }} strokeWidth={3} />
        ) : (
          <span className="w-2 h-2 rounded-full" style={{ background: 'var(--cool-2)', opacity: 0.5 }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">{title}</p>
        {sub && <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5 line-clamp-2">{sub}</p>}
        {summary ? (
          <p className="text-[12.5px] font-semibold mt-1.5 flex items-center gap-1.5" style={{ color: summary.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: summary.color }} />
            {summary.text}
          </p>
        ) : (
          <p className="text-xs mt-1.5" style={{ color: 'var(--cool-2)' }}>
            Tap to choose
          </p>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)] shrink-0 mt-2" />
    </button>
  );
}

function KeyDate({ label, date }: { label: string; date: string }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span>{label}</span>
      <span className="font-semibold tabular-nums" style={{ color: 'var(--primary)' }}>
        {fmtDateShort(date)}
      </span>
    </div>
  );
}

function RaceSheet({
  race,
  picked,
  onPick,
  onClear,
  onView,
}: {
  race: BallotRace;
  picked?: string;
  onPick: (id: string) => void;
  onClear: () => void;
  onView: (id: string) => void;
}) {
  return (
    <div className="px-5 pb-4">
      <h2 className="text-xl font-extrabold tracking-tight">{race.title}</h2>
      <p className="text-sm text-[color:var(--muted-foreground)] mt-1">Choose one candidate.</p>

      <div className="mt-4 space-y-2">
        {race.candidates.map((c) => {
          const on = picked === c.id;
          return (
            <div
              key={c.id}
              className="rounded-xl border p-3 flex items-center gap-3"
              style={on ? { borderColor: 'var(--primary)', background: 'var(--accent)' } : { borderColor: 'var(--border)' }}
            >
              <button
                onClick={() => onPick(c.id)}
                className="w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0"
                style={{ borderColor: on ? 'var(--primary)' : 'var(--border)', background: on ? 'var(--primary)' : 'transparent' }}
              >
                {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
              </button>
              <button onClick={() => onPick(c.id)} className="flex-1 text-left">
                <p className="text-sm font-bold">{c.full_name}</p>
                <p className="text-xs text-[color:var(--muted-foreground)] flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: partyColor(c.party_affiliation) }} />
                  {formatPartyDisplay(c.party_affiliation) || 'Unaffiliated'}
                  {c.is_incumbent ? ' · Incumbent' : ''}
                </p>
              </button>
              <button
                onClick={() => onView(c.id)}
                className="text-xs font-semibold hover-elevate rounded-md px-2 py-1"
                style={{ color: 'var(--primary)' }}
              >
                Details
              </button>
            </div>
          );
        })}
      </div>

      {picked && (
        <Button variant="ghost" className="w-full mt-3" onClick={onClear}>
          Clear selection
        </Button>
      )}
    </div>
  );
}

function MeasureSheet({
  measure,
  picked,
  onPick,
  onClear,
}: {
  measure: BallotMeasure;
  picked?: string;
  onPick: (v: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="px-5 pb-4">
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="default">{initiativeNumberLine(measure) || 'Ballot measure'}</Badge>
        {measure.measure_type && <Badge variant="secondary">{measure.measure_type}</Badge>}
      </div>
      <h2 className="text-xl font-extrabold tracking-tight leading-tight">
        {initiativeDisplayName(measure)}
      </h2>
      {measure.description && (
        <div className="mt-3">
          <p className="text-xs font-bold uppercase tracking-wide text-[color:var(--muted-foreground)]">SOS full title</p>
          <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-[color:var(--foreground)]">
            {measure.description}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mt-5">
        {(['yes', 'no'] as const).map((v) => {
          const on = picked === v;
          const color = v === 'yes' ? '#15803D' : '#B91C1C';
          return (
            <button
              key={v}
              onClick={() => onPick(v)}
              className="rounded-xl border-2 py-4 font-bold text-base hover-elevate active-elevate-2"
              style={on ? { borderColor: color, background: `${color}14`, color } : { borderColor: 'var(--border)' }}
            >
              {on && <Check className="w-4 h-4 inline mr-1.5 -mt-0.5" strokeWidth={3} />}
              Vote {v.toUpperCase()}
            </button>
          );
        })}
      </div>

      {measure.full_text_url && (
        <a
          href={measure.full_text_url}
          target="_blank"
          rel="noreferrer"
          className="block text-center text-sm font-semibold py-3 mt-2 hover-elevate rounded-md"
          style={{ color: 'var(--primary)' }}
        >
          Read full text →
        </a>
      )}

      {picked && (
        <Button variant="ghost" className="w-full mt-1" onClick={onClear}>
          Clear selection
        </Button>
      )}
    </div>
  );
}
